import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "../lib/api.js";
import type { PlacementHistoryResponse } from "../lib/types.js";
import { useMe } from "./useMe.js";

/**
 * Has the signed-in caller completed the placement test at least once?
 *
 * "Completed" = at least one row in GET /v1/me/placements/history (a placement
 * row is only written when a diagnostic run finalizes). We fetch a single row
 * (limit=1) purely as an existence check, and ONLY when signed in — anonymous
 * landing-page visitors never hit the endpoint, so the marketing pages stay as
 * cheap as before.
 *
 * Drives the post-placement CTA swap: once a user has placed, we stop nudging
 * them to (re)take the test and instead show "Subscribe Now" (free) or send
 * them to the arcade (premium). This `limit: 1` existence check is a DISTINCT
 * cache entry from usePlacementHistory's `limit: 20` list, so the two fetch
 * independently (the extra request is tiny). What they share is the
 * `["me","placements", …]` key prefix — that's what lets the completion
 * invalidation in DiagnosticPlayShell refresh both in one shot.
 */
export function usePlacementDone(): {
  done: boolean;
  /** A started-but-unfinished placement run exists → offer "Resume placement". */
  inProgress: boolean;
  loading: boolean;
  signedIn: boolean;
} {
  const { data: me } = useMe();
  const signedIn = !!me?.user;
  const q = useQuery<PlacementHistoryResponse>({
    queryKey: ["me", "placements", "history", { limit: 1, offset: 0 }],
    queryFn: () => apiFetch<PlacementHistoryResponse>("/v1/me/placements/history?limit=1&offset=0"),
    enabled: signedIn,
    staleTime: 60_000,
  });
  const done = signedIn && (q.data?.rows?.length ?? 0) > 0;
  return {
    // A disabled query reports `isPending`, so gate every field on `signedIn`:
    // a signed-out visitor is never "done" and is never "loading". Guard `rows`
    // too (not just `data`) so a malformed/unexpected payload can't throw during
    // render in the always-mounted nav that consumes this.
    done,
    // Only meaningful before they've completed one: a finished placement takes
    // precedence (subscribe/play), so callers check `done` first.
    inProgress: signedIn && !done && q.data?.in_progress === true,
    loading: signedIn && q.isPending,
    signedIn,
  };
}
